import { useEffect, useRef } from 'react';
import { debounce } from 'lodash';

/**
 * Custom hook for infinite scroll lazy loading
 * Handles both scroll-based loading and automatic loading when content doesn't fill container
 * 
 * @param {Object} options
 * @param {Function} options.onLoadMore - Callback to load more items
 * @param {boolean} options.hasMore - Whether there are more items to load
 * @param {boolean} options.loadingMore - Whether currently loading more items
 * @param {Array} options.items - The array of items being displayed
 * @param {number} options.threshold - Distance from bottom (in px) to trigger load (default: 100)
 * @returns {Object} - Returns { scrollContainerRef } to attach to the scrollable container
 */
const useInfiniteScroll = ({
  onLoadMore,
  hasMore,
  loadingMore,
  items,
  threshold = 100
}) => {
  const scrollContainerRef = useRef(null);

  // Handle scroll-based lazy loading
  useEffect(() => {
    if (!hasMore || !onLoadMore) return;

    const debouncedHandleScroll = debounce(() => {
      if (loadingMore) return;

      const element = scrollContainerRef.current;
      if (!element) return;

      const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      if (distanceFromBottom <= threshold) {
        onLoadMore();
      }
    }, 100);

    const element = scrollContainerRef.current;
    if (element) {
      element.addEventListener('scroll', debouncedHandleScroll);
      return () => {
        element.removeEventListener('scroll', debouncedHandleScroll);
        debouncedHandleScroll.cancel();
      };
    }
  }, [hasMore, loadingMore, onLoadMore, threshold]);

  // Check if content fills container, if not load more automatically
  useEffect(() => {
    if (!hasMore || !onLoadMore || loadingMore) return;

    const checkContentHeight = () => {
      const element = scrollContainerRef.current;
      if (!element) return;

      // Only trigger if content doesn't overflow (no scrollbar needed)
      const hasOverflow = element.scrollHeight > element.clientHeight;
      if (!hasOverflow) {
        onLoadMore();
      }
    };

    // Delay check to ensure DOM is updated after items change
    const timeoutId = setTimeout(checkContentHeight, 150);

    return () => clearTimeout(timeoutId);
  }, [hasMore, onLoadMore, loadingMore, items]);

  return { scrollContainerRef };
};

export default useInfiniteScroll;
